/**
 * Author: Frontend Team
 * Generation: Animation Event Mocking for Testing
 * Version: v3.0.124
 * Modified: 2025-09-14
 */

import webSocketService from '../services/websocket/WebSocketService';

export interface MockEventResult {
  eventType: string;
  agentId: string;
  data: any;
  timestamp: string;
}

export class AnimationEventMocker {
  private testAgents = ['test_agent_1', 'test_agent_2', 'test_agent_3'];
  private currentCombatId = 0;

  private generateCombatId(): string {
    return `combat_${++this.currentCombatId}`;
  }

  private getRandomAgent(): string {
    return this.testAgents[Math.floor(Math.random() * this.testAgents.length)];
  }

  private getTwoAgents(): { attacker: string; defender: string } {
    const attacker = this.testAgents[0];
    const defender = this.testAgents[1];
    return { attacker, defender };
  }

  triggerEvent(eventType: string): MockEventResult {
    const timestamp = new Date().toISOString();
    let agentId: string;
    let data: any;

    console.log(`🎯 AnimationEventMocker: Triggering ${eventType}`);

    switch (eventType) {
      // === Lifecycle Events ===
      case 'AGENT_SPAWNED':
        agentId = this.getRandomAgent();
        data = {
          agent: {
            id: agentId,
            position: { x: 5, y: 5 },
            color: 'blue',
            health: 100
          }
        };
        break;

      case 'AGENT_MOVED':
        agentId = this.getRandomAgent();
        data = {
          agentId,
          position: { x: 6, y: 5 },
          from: { x: 5, y: 5 },
          to: { x: 6, y: 5 }
        };
        break;

      case 'AGENT_ELIMINATED':
        agentId = this.getRandomAgent();
        data = {
          agentId,
          eliminatedBy: 'test_agent_2',
          reason: 'combat'
        };
        break;

      // === Combat Events ===
      case 'COMBAT_STARTED':
        const combatAgents = this.getTwoAgents();
        agentId = combatAgents.attacker;
        data = {
          combatId: this.generateCombatId(),
          attackerId: combatAgents.attacker,
          defenderId: combatAgents.defender,
          attackerColor: 'red',
          defenderColor: 'blue'
        };
        break;

      case 'COMBAT_DAMAGE_DEALT':
        const damageAgents = this.getTwoAgents();
        agentId = damageAgents.attacker;
        data = {
          combatId: `combat_${this.currentCombatId}`,
          attackerId: damageAgents.attacker,
          defenderId: damageAgents.defender,
          damage: 25,
          isCritical: Math.random() > 0.7,
          attackerColor: 'red',
          defenderColor: 'blue',
          weaponUsed: Math.random() > 0.5 ? { type: 'SWORD' } : null
        };
        break;

      case 'COMBAT_ATTACK_MISSED':
        const missAgents = this.getTwoAgents();
        agentId = missAgents.attacker;
        data = {
          combatId: `combat_${this.currentCombatId}`,
          attackerId: missAgents.attacker,
          defenderId: missAgents.defender
        };
        break;

      case 'COMBAT_ENDED':
        const endAgents = this.getTwoAgents();
        agentId = endAgents.attacker;
        data = {
          combatId: `combat_${this.currentCombatId}`,
          winner: endAgents.attacker,
          loser: endAgents.defender,
          reason: 'health_depleted'
        };
        break;

      // === Resource Events ===
      case 'RESOURCE_COLLECTED':
        agentId = this.getRandomAgent();
        const resourceType = Math.random() > 0.5 ? 'WEAPON' : 'MEDICAL';
        data = {
          agentId,
          resourceId: `resource_${Date.now()}`,
          type: resourceType,
          resourceType: resourceType,
          position: { x: 7, y: 6 }
        };
        break;

      case 'WEAPON_EQUIPPED':
        agentId = this.getRandomAgent();
        data = {
          agentId,
          weaponId: `weapon_${Date.now()}`,
          weaponType: 'SWORD',
          previousWeapon: null
        };
        break;

      case 'ITEM_USED':
        agentId = this.getRandomAgent();
        data = {
          agentId,
          itemId: `item_${Date.now()}`,
          itemType: 'HEALTH_POTION',
          effect: { health: 50 }
        };
        break;

      // === Environment Events ===
      case 'SAFEZONE_DAMAGE_APPLIED':
        agentId = this.getRandomAgent();
        data = {
          agentId,
          damage: 10,
          currentRadius: 15,
          newRadius: 12,
          position: { x: 8, y: 8 }
        };
        break;

      default:
        throw new Error(`Unknown event type: ${eventType}`);
    }

    // Trigger the mock event through WebSocketService
    console.log(`📡 Sending mock event: ${eventType}`, { agentId, data });
    webSocketService.triggerMockEvent(eventType, data);

    return {
      eventType,
      agentId,
      data,
      timestamp
    };
  }

  // Convenience methods for testing sequences
  triggerCombatSequence(): MockEventResult[] {
    const results: MockEventResult[] = [];

    // 1. Combat Started
    results.push(this.triggerEvent('COMBAT_STARTED'));

    // Wait and then damage
    setTimeout(() => {
      results.push(this.triggerEvent('COMBAT_DAMAGE_DEALT'));
    }, 1000);

    // Wait and then end combat
    setTimeout(() => {
      results.push(this.triggerEvent('COMBAT_ENDED'));
    }, 2000);

    return results;
  }

  triggerMovementSequence(): MockEventResult[] {
    const results: MockEventResult[] = [];

    // 1. Agent spawned
    results.push(this.triggerEvent('AGENT_SPAWNED'));

    // 2. Agent moved
    setTimeout(() => {
      results.push(this.triggerEvent('AGENT_MOVED'));
    }, 500);

    return results;
  }

  triggerResourceSequence(): MockEventResult[] {
    const results: MockEventResult[] = [];

    // 1. Resource collected (weapon)
    results.push(this.triggerEvent('RESOURCE_COLLECTED'));

    // 2. Weapon equipped
    setTimeout(() => {
      results.push(this.triggerEvent('WEAPON_EQUIPPED'));
    }, 500);

    // 3. Item used
    setTimeout(() => {
      results.push(this.triggerEvent('ITEM_USED'));
    }, 1000);

    return results;
  }

  // Reset state for clean testing
  reset(): void {
    this.currentCombatId = 0;
    console.log('🔄 AnimationEventMocker: State reset');
  }

  // Get current test agents
  getTestAgents(): string[] {
    return [...this.testAgents];
  }

  // Add custom test agent
  addTestAgent(agentId: string): void {
    if (!this.testAgents.includes(agentId)) {
      this.testAgents.push(agentId);
      console.log(`➕ Added test agent: ${agentId}`);
    }
  }

  // Debug info
  getDebugInfo(): any {
    return {
      testAgents: this.testAgents,
      currentCombatId: this.currentCombatId,
      mockModeEnabled: webSocketService.isMockMode()
    };
  }
}